// Registration landing page for the Black Friday webinar (SK/CZ e-shops).
// Intentionally hidden: not linked from navigation, not in sitemap.xml, and marked noindex.
// Every promo link should carry ?src=<channel> (e.g. ?src=linkedin, ?src=partner-saec, ?src=osobne-martin)
// so registrations in ClickUp can be split by source.
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { CalendarDays, CalendarPlus, CheckCircle2, Clock, Video } from 'lucide-react';
import { trackFormSubmission } from '@/utils/analytics';
import useScrollReveal from '@/hooks/useScrollReveal';

// Webinar facts — change here and everything on the page (incl. the calendar entry) follows
export const WEBINAR = {
  id: 'bf-2026',
  title: 'Black Friday, ktorý zarába',
  // Local Bratislava/Prague time; November = CET (UTC+1)
  startUtc: '2026-11-10T09:00:00Z',
  endUtc: '2026-11-10T10:00:00Z',
  dateLabel: 'utorok 10. 11. 2026',
  timeLabel: '10:00',
  durationLabel: '45 min + Q&A',
};

const MARTIN_PHOTO = '/lovable-uploads/martin-faith.jpg';
const MARTIN_BIO = 'Martin Faith, zakladateľ a CEO Stratodata, pomáha e-shopom a retailovým firmám spojiť dáta z marketingu, webu, ERP a skladu do jedného čísla, podľa ktorého sa dá riadiť zisk.';
const GUEST = {
  name: 'Jakub Žilinčan',
  role: 'Zakladateľ XEMEA',
  photo: '/lovable-uploads/jakub-zilincan.jpg',
  bio: 'Jakub Žilinčan, zakladateľ XEMEA, pomáha väčším e-shopom ako fractional CMO prepojiť marketing s financiami a ziskovo rásť po Európe.',
};

// Google Apps Script web app that appends each registration to the "Webinar registrations" sheet (Petra's Drive).
// This is the only place registrations go: they are not sent to the CRM / ClickUp.
const SHEET_WEBHOOK = 'https://script.google.com/macros/s/AKfycbxZ4LsvY3dRscA2L4Xv2mlNB4__WgGidSAw2-GqJUa0aBqVtzv09xWwOTvxHI215BiI/exec';

const roles = ['Majiteľ / CEO', 'E-commerce manažér', 'Marketing', 'Financie', 'Iné'] as const;

const agenda = [
  {
    step: 'Čistenie skladu',
    text: 'Ktoré produkty sa oplatí zlacniť, ktoré radšej nie a ako nepáliť reklamu na tovar, ktorý dochádza.',
  },
  {
    step: 'Obrat',
    text: 'Koľko obratu Black Friday naozaj pridá a koľko si len požičia z iných týždňov.',
  },
  {
    step: 'Noví zákazníci',
    text: 'Ako zmerať, či je zákazník z Black Friday lovec zliav, alebo začiatok vzťahu.',
  },
  {
    step: 'Profitabilita',
    text: 'Kedy kampaň prestáva zarábať a kedy je čas pribrzdiť.',
  },
  {
    step: 'Timing',
    text: 'Kedy sezóna začína a končí a prečo to nie je na každom trhu rovnako.',
  },
  {
    step: 'Vyhodnotenie',
    text: 'Ako zistiť, čo vám Black Friday skutočne priniesol. S ukážkou naživo v RAS.',
  },
];

// Illustrative BF season curve (index 0–100, not real client numbers). Index 7 = Black Friday, 10 = Cyber Monday.
// Shape follows Martin's program: demand stalls the week before BF (customers wait for discounts) while ad spend
// already climbs, and after the season spend keeps running while demand drops.
const ORDERS = [24, 26, 30, 34, 32, 30, 34, 100, 92, 86, 88, 40, 28, 22, 20];
const SPEND = [18, 20, 24, 30, 40, 50, 62, 94, 88, 84, 82, 78, 72, 66, 56];
const makeGeom = (w: number) => {
  const CHART = { w, h: 300, left: 24, right: w - 24, top: 36, bottom: 236 };
  const px = (i: number) => CHART.left + (i * (CHART.right - CHART.left)) / (ORDERS.length - 1);
  const py = (v: number) => CHART.bottom - (v / 100) * (CHART.bottom - CHART.top);
  // Smooth Catmull-Rom curve through values[from..to], using the full series for tangents
  const curve = (values: number[], from = 0, to = values.length - 1, move = true) => {
  const p = (i: number) => [px(Math.max(0, Math.min(values.length - 1, i))), py(values[Math.max(0, Math.min(values.length - 1, i))])];
  let d = move ? `M${p(from)[0]},${p(from)[1]}` : `L${p(from)[0]},${p(from)[1]}`;
  const step = from <= to ? 1 : -1;
  for (let i = from; i !== to; i += step) {
    const [x0, y0] = p(i - step), [x1, y1] = p(i), [x2, y2] = p(i + step), [x3, y3] = p(i + 2 * step);
    d += ` C${x1 + (x2 - x0) / 6},${y1 + (y2 - y0) / 6} ${x2 - (x3 - x1) / 6},${y2 - (y3 - y1) / 6} ${x2},${y2}`;
  }
  return d;
  };
  return { CHART, px, py, curve };
};

const phases = [
  { label: 'Príprava', from: 0, to: 6.5 },
  { label: 'Black Friday týždeň', from: 6.5, to: 10.5, highlight: true },
  { label: 'Po Black Friday', from: 10.5, to: 14 },
];

// Which program steps sit under which chart phase (variant A); read column by column = Martin's running order
const PHASE_STEPS = [[0, 1], [2, 3], [4, 5]];

const BfSeasonChart = ({ program = false }: { program?: boolean }) => {
  // Re-mount the SVG every few seconds so the story replays while the visitor reads
  const [run, setRun] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let timer: number | undefined;
    const io = new IntersectionObserver(([entry]) => {
      window.clearInterval(timer);
      if (entry.isIntersecting) {
        setRun((r) => r + 1);
        timer = window.setInterval(() => setRun((r) => r + 1), 11000);
      }
    }, { threshold: 0.35 });
    io.observe(el);
    return () => { io.disconnect(); window.clearInterval(timer); };
  }, []);

  const { CHART, px, py, curve } = makeGeom(program ? 1100 : 640);
  // Loss = wherever spend runs above orders: area under the spend curve, clipped to the area above the orders curve
  const lossId = `bf-loss-${program ? 'p' : 'c'}`;
  const underSpend = `${curve(SPEND)} L${CHART.right},${CHART.bottom} L${CHART.left},${CHART.bottom} Z`;
  const aboveOrders = `${curve(ORDERS)} L${CHART.right},0 L${CHART.left},0 Z`;

  return (
    <div ref={ref} className="rounded-2xl bg-[#2A1E47] text-white shadow-xl shadow-[#2A1E47]/25 p-5 md:p-6">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#FF6B35] mb-2">Kde sa strácajú peniaze</p>
      <p className="text-lg md:text-xl font-semibold leading-snug mb-1">Najviac peňazí sa nestráca na Black Friday, ale pred ním a po ňom.</p>
      <p className="text-sm text-white/70 mb-4">Tak to vidíme v dátach e-shopov, s ktorými pracujeme.</p>
      <style>{`
        .bf-draw { stroke-dasharray: 1; stroke-dashoffset: 1; animation: bf-draw 2.2s ease-out forwards; }
        .bf-fade { opacity: 0; animation: bf-fade .6s ease-out forwards; }
        .bf-pulse { transform-box: fill-box; transform-origin: center; animation: bf-pulse 1.6s ease-in-out infinite; }
        @keyframes bf-draw { to { stroke-dashoffset: 0; } }
        @keyframes bf-fade { to { opacity: 1; } }
        @keyframes bf-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.8); opacity: .35; } }
        @media (prefers-reduced-motion: reduce) {
          .bf-draw { animation: none; stroke-dashoffset: 0; }
          .bf-fade { animation: none; opacity: 1; }
          .bf-pulse { animation: none; }
        }
      `}</style>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2 text-xs text-white/70">
        <span className="inline-flex items-center gap-2"><span className="w-4 h-[3px] rounded bg-[#FF6B35]" />Objednávky (dopyt)</span>
        <span className="inline-flex items-center gap-2"><span className="w-4 h-[3px] rounded bg-white" />Výdavky na reklamu</span>
      </div>
      <svg key={run} viewBox={`0 0 ${CHART.w} ${CHART.h}`} className="w-full h-auto" role="img"
        aria-label="Ilustračný priebeh: týždeň pred Black Friday objednávky stoja, kým výdavky na reklamu rastú; po sezóne objednávky klesnú, ale reklama beží ďalej. Oba rozdiely sú strata.">
        <defs>
          <clipPath id={lossId}><path d={aboveOrders} /></clipPath>
        </defs>
        {/* Phase bands */}
        {phases.map((ph, i) => (
          <g key={ph.label} className="bf-fade" style={{ animationDelay: `${i * 0.15}s` }}>
            <rect x={px(ph.from)} y={CHART.top - 16} width={px(ph.to) - px(ph.from)} height={CHART.bottom - CHART.top + 16}
              fill={ph.highlight ? 'rgba(255,107,53,0.10)' : 'rgba(255,255,255,0.03)'} />
            <text x={(px(ph.from) + px(ph.to)) / 2} y={CHART.bottom + 30} textAnchor="middle"
              className="fill-white/80" style={{ fontSize: 13, fontWeight: 600 }}>{ph.label}</text>
          </g>
        ))}
        <line x1={CHART.left} x2={CHART.right} y1={CHART.bottom} y2={CHART.bottom} stroke="rgba(255,255,255,0.25)" />

        {/* Day markers */}
        {[{ i: 7, t: 'Black Friday' }, { i: 10, t: 'Cyber Monday' }].map((m) => (
          <g key={m.t} className="bf-fade" style={{ animationDelay: '0.4s' }}>
            <line x1={px(m.i)} x2={px(m.i)} y1={CHART.top - 4} y2={CHART.bottom} stroke="rgba(255,255,255,0.25)" strokeDasharray="3 4" />
            <text x={px(m.i)} y={CHART.top - 20} textAnchor="middle" className="fill-white/60" style={{ fontSize: 11 }}>{m.t}</text>
          </g>
        ))}

        {/* Loss areas: before BF (customers wait for discounts) and after the season (spend keeps running) */}
        <path d={underSpend} clipPath={`url(#${lossId})`} fill="rgba(239,68,68,0.35)" className="bf-fade" style={{ animationDelay: '3.2s' }} />

        <path d={curve(ORDERS)} pathLength={1} fill="none" stroke="#FF6B35" strokeWidth={3.5} strokeLinecap="round" className="bf-draw" style={{ animationDelay: '0.4s' }} />
        <path d={curve(SPEND)} pathLength={1} fill="none" stroke="#FFFFFF" strokeWidth={3} strokeLinecap="round" className="bf-draw" style={{ animationDelay: '1.1s' }} />

        {/* Callouts, one per loss area */}
        {[
          { i: 5, title: 'Pred Black Friday', sub: 'Zákazníci čakajú na zľavy' },
          { i: 12.5, title: 'Po sezóne', sub: 'Reklama beží, dopyt už nie' },
        ].map((c) => (
          <g key={c.title} className="bf-fade" style={{ animationDelay: '3.6s' }}>
            <rect x={px(c.i) - 80} y={py(SPEND[Math.round(c.i)]) - 58} width={160} height={40} rx={8} fill="#EF4444" />
            <text x={px(c.i)} y={py(SPEND[Math.round(c.i)]) - 41} textAnchor="middle" className="fill-white" style={{ fontSize: 12, fontWeight: 700 }}>{c.title}</text>
            <text x={px(c.i)} y={py(SPEND[Math.round(c.i)]) - 26} textAnchor="middle" className="fill-white/90" style={{ fontSize: 10 }}>{c.sub}</text>
          </g>
        ))}
      </svg>
      {program && (
        <div className="mt-1 border-t border-white/10 pt-5" style={{ paddingLeft: '3.75%', paddingRight: '3.75%' }}>
        <p className="text-sm font-semibold uppercase tracking-wide text-[#FF6B35] mb-4">Program:</p>
        <div className="grid grid-cols-1 md:grid-cols-[46fr_29fr_25fr] gap-x-4 gap-y-5">
          {PHASE_STEPS.map((steps, col) => (
            <div key={col} className={col === 1 ? 'space-y-4 md:border-l md:border-[#FF6B35]/40 md:pl-4' : col === 2 ? 'space-y-4 md:border-l md:border-white/15 md:pl-4' : 'space-y-4'}>
              {steps.map((i) => (
                <div key={agenda[i].step}>
                  <p className="text-xs font-bold text-[#FF6B35]">0{i + 1}</p>
                  <p className="text-sm font-bold text-white leading-snug">{agenda[i].step}</p>
                  <p className="text-xs text-white/65 leading-relaxed mt-0.5">{agenda[i].text}</p>
                </div>
              ))}
              {col === 1 && (
                <div>
                  <p className="text-xs font-bold text-[#FF6B35]">Hosť</p>
                  <p className="text-sm font-bold text-white leading-snug">{GUEST.name}</p>
                  <p className="text-xs text-white/65 leading-relaxed mt-0.5">a jeho prístup k Black Friday z praxe.</p>
                </div>
              )}
              {col === 2 && (
                <div>
                  <p className="text-xs font-bold text-[#FF6B35]">Q&amp;A</p>
                  <p className="text-sm font-bold text-white leading-snug">Vaše otázky</p>
                  <p className="text-xs text-white/65 leading-relaxed mt-0.5">Martin a Jakub odpovedajú aj k vašim číslam.</p>
                </div>
              )}
            </div>
          ))}
        </div>
        </div>
      )}
      <p className="mt-4 text-[11px] text-white/45">Ilustračný priebeh, nie skutočné čísla konkrétneho klienta.</p>
    </div>
  );
};

// Hero layout values. Petra tunes them locally via /webinar?ladit=1 and sends the copied JSON back.
const LAYOUT_DEFAULTS = {
  maxWidth: 1216, // px, content width of the whole page (= stratodata.eu container-custom: 1280 minus padding)
  h1Max: 96, // px, headline size on large screens (site hero h1 is 72px; campaign page a step bigger)
  headGap: 80, // px, space between the subtitle and the text/chart row
  chartShift: 8, // px, extra vertical offset of the chart (negative = up)
  leftCol: 1, // fr, width share of the text column
  rightCol: 1.2, // fr, width share of the chart column
};
type Layout = typeof LAYOUT_DEFAULTS;

const layoutVars = (l: Layout) =>
  ({
    '--maxw': `${l.maxWidth}px`,
    '--h1max': `${l.h1Max}px`,
    '--headgap': `${l.headGap}px`,
    '--chartshift': `${l.chartShift}px`,
    '--cols': `${l.leftCol}fr ${l.rightCol}fr`,
  }) as React.CSSProperties;

const knobs: { key: keyof Layout; label: string; min: number; max: number; step: number; unit: string }[] = [
  { key: 'h1Max', label: 'Veľkosť nadpisu', min: 60, max: 200, step: 2, unit: 'px' },
  { key: 'headGap', label: 'Medzera pod podnadpisom', min: 0, max: 120, step: 2, unit: 'px' },
  { key: 'chartShift', label: 'Posun grafu hore/dole', min: -200, max: 200, step: 2, unit: 'px' },
  { key: 'leftCol', label: 'Šírka stĺpca s textom', min: 0.5, max: 2, step: 0.05, unit: 'fr' },
  { key: 'rightCol', label: 'Šírka stĺpca s grafom', min: 0.5, max: 2.5, step: 0.05, unit: 'fr' },
  { key: 'maxWidth', label: 'Šírka stránky', min: 1000, max: 2000, step: 20, unit: 'px' },
];

// Dev-only tuning panel (never rendered in the production build)
const LayoutTuner = ({ layout, setLayout }: { layout: Layout; setLayout: (l: Layout) => void }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-h-[85vh] overflow-auto rounded-xl bg-white shadow-2xl border border-gray-200 p-4 text-sm text-[#2A1E47]">
      <p className="font-bold mb-1">Ladenie rozloženia</p>
      <p className="text-xs text-muted-foreground mb-3">Vidíš len ty lokálne. Na web sa to nedostane.</p>
      {knobs.map((k) => (
        <label key={k.key} className="block mb-3">
          <span className="flex justify-between text-xs font-medium mb-1">
            <span>{k.label}</span>
            <span className="tabular-nums">{layout[k.key]} {k.unit}</span>
          </span>
          <input
            type="range" min={k.min} max={k.max} step={k.step} value={layout[k.key]}
            onChange={(e) => setLayout({ ...layout, [k.key]: Number(e.target.value) })}
            className="w-full accent-[#FF6B35]"
          />
        </label>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { navigator.clipboard.writeText(JSON.stringify(layout)); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="flex-1 px-3 py-2 rounded-lg bg-[#FF6B35] text-white font-semibold"
        >
          {copied ? 'Skopírované ✓' : 'Skopírovať nastavenia'}
        </button>
        <button type="button" onClick={() => setLayout(LAYOUT_DEFAULTS)} className="px-3 py-2 rounded-lg border border-gray-300">
          Reset
        </button>
      </div>
    </div>
  );
};

const icsDate = (iso: string) => iso.replace(/[-:]/g, '').replace('.000', '');

const calendarDetails =
  'Bezplatný webinár Stratodata. Odkaz na pripojenie (Google Meet) vám pošleme e-mailom deň pred webinárom.';

export const googleCalendarUrl = () => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `Webinár: ${WEBINAR.title}`,
    dates: `${icsDate(WEBINAR.startUtc)}/${icsDate(WEBINAR.endUtc)}`,
    details: calendarDetails,
    location: 'Online (Google Meet)',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const downloadIcs = () => {
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Stratodata//Webinar//SK',
    'BEGIN:VEVENT',
    `UID:${WEBINAR.id}@stratodata.eu`,
    `DTSTAMP:${icsDate(new Date().toISOString().split('.')[0] + 'Z')}`,
    `DTSTART:${icsDate(WEBINAR.startUtc)}`,
    `DTEND:${icsDate(WEBINAR.endUtc)}`,
    `SUMMARY:Webinár: ${WEBINAR.title}`,
    `DESCRIPTION:${calendarDetails}`,
    'LOCATION:Online (Google Meet)',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'webinar-stratodata.ics';
  a.click();
  URL.revokeObjectURL(url);
};

const Webinar = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Which channel the visitor came from, so registrations in ClickUp can be told apart
  const source = searchParams.get('src') || searchParams.get('utm_source') || 'direct';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [photoOk, setPhotoOk] = useState(true);
  const [layout, setLayout] = useState<Layout>(LAYOUT_DEFAULTS);
  const showTuner = import.meta.env.DEV && searchParams.has('ladit');
  useScrollReveal();

  const formSchema = z.object({
    name: z.string().min(2, { message: 'Zadajte prosím svoje meno' }),
    email: z.string().email({ message: 'Zadajte prosím platný e-mail' }),
    company: z.string().min(2, { message: 'Zadajte prosím názov firmy' }),
    role: z.string().min(1, { message: 'Vyberte prosím pozíciu' }),
    consent: z.literal(true, { errorMap: () => ({ message: 'Bez súhlasu vás nevieme zaregistrovať' }) }),
    marketingConsent: z.boolean().optional(),
    website: z.string().optional(), // honeypot, hidden from humans
  });
  type WebinarData = z.infer<typeof formSchema>;

  const form = useForm<WebinarData>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', email: '', company: '', role: '', consent: false as unknown as true, marketingConsent: false, website: '' },
  });

  const onSubmit = async (data: WebinarData) => {
    // Bots fill the hidden field; pretend success without sending anything
    if (data.website) {
      navigate('/webinar/dakujeme');
      return;
    }
    setIsSubmitting(true);
    try {
      // Registrations go only to the Google Sheet (not to the CRM / ClickUp).
      // text/plain keeps it a simple request (no CORS preflight); the script answers "ok" after appending the row.
      const yesNo = (v?: boolean) => (v ? 'yes' : 'no');
      const row = {
        name: data.name,
        company: data.company,
        email: data.email,
        phone_number: '',
        text: [
          `[Webinar registration – ${WEBINAR.id}]`,
          `Webinar: ${WEBINAR.title} (${WEBINAR.dateLabel}, ${WEBINAR.timeLabel})`,
          `Role: ${data.role}`,
          `Source: ${source}`,
          `Marketing consent: ${yesNo(data.marketingConsent)}`,
          'Language: sk',
        ].join('\n'),
      };
      const response = await fetch(SHEET_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(row),
      });
      const answer = await response.text();
      if (!response.ok || answer.trim() !== 'ok') throw new Error(`Sheet: HTTP ${response.status} ${answer.slice(0, 100)}`);

      form.reset();
      trackFormSubmission('webinar_registration_form');
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({ event: 'contact_form_success', form_name: 'webinar_registration_form', webinar: WEBINAR.id, source });
      navigate('/webinar/dakujeme');
    } catch (error) {
      console.error('Error submitting webinar registration:', error);
      toast.error('Niečo sa pokazilo. Skúste to znova alebo napíšte na hello@stratodata.eu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const facts = [
    { icon: CalendarDays, label: WEBINAR.dateLabel },
    { icon: Clock, label: `${WEBINAR.timeLabel} · ${WEBINAR.durationLabel}` },
    { icon: Video, label: 'Online · zadarmo' },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-white" style={layoutVars(layout)}>
      {showTuner && <LayoutTuner layout={layout} setLayout={setLayout} />}
      <SEO
        title="Webinár: Black Friday, ktorý zarába"
        description="Bezplatný webinár Stratodata: ako pomocou dát naplánovať kampane na Black Friday, priebežne riadiť ich výkonnosť a vyhodnotiť, aký zisk vám skutočne priniesli."
        canonical="/webinar"
        noIndex
      />

      {/* Slim header: logo only, the page is a focused campaign landing */}
      <header className="px-6 md:px-10 py-5 md:py-6 border-b border-gray-100">
        <div className="max-w-[var(--maxw)] mx-auto flex items-center justify-between">
          <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
            <img src="/lovable-uploads/2bbaa664-89c2-45e5-80a8-fd888d8066c0.png" alt="Stratodata" className="h-8 md:h-10" />
          </Link>
          <a href="#registracia" className="hidden sm:inline-flex px-4 py-2 rounded-lg border border-[#2A1E47] text-sm font-medium text-[#2A1E47] hover:bg-[#2A1E47] hover:text-white transition-colors">
            Registrovať sa
          </a>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero: copy on top, the season chart with the program built into its phases below */}
        <section className="relative px-6 md:px-10 py-10 md:py-16 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-[#FFE3CC] rounded-full filter blur-3xl opacity-70 pointer-events-none" />
          <div className="absolute top-1/2 -left-32 w-[360px] h-[360px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-50 pointer-events-none" />

          <div className="relative z-10 max-w-[var(--maxw)] mx-auto">
            <div className="grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-14 items-end mb-[var(--headgap)] reveal">
              <div className="[container-type:inline-size]">
              <p className="inline-block px-3 py-1 mb-4 rounded-full bg-[#FF6B35]/10 text-xs font-semibold uppercase tracking-wide text-[#FF6B35]">
                Bezplatný webinár
              </p>
              <h1 className="text-[clamp(3rem,15cqw,var(--h1max))] font-bold tracking-tight text-[#2A1E47] leading-[0.95] mb-6">
                Black Friday, ktorý{' '}
                <span className="relative inline-block text-[#FF6B35]">
                  zarába
                  {/* Hand-drawn underline */}
                  <svg className="absolute left-0 -bottom-[0.18em] w-full h-[0.28em] overflow-visible" viewBox="0 0 200 20" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M3 14 C 50 4, 120 3, 197 9" fill="none" stroke="#FF6B35" strokeWidth={5} strokeLinecap="round" />
                  </svg>
                </span>
              </h1>
              <p className="text-2xl md:text-4xl font-semibold text-[#2A1E47]">Ako riadiť kampane na základe dát</p>
              </div>
              <div>
                <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-6">
                  Black Friday prinesie rekordný obrat. Otázka je, koľko z neho vám reálne zostane. Zistite, ako pomocou dát naplánovať kampane na Black Friday, priebežne riadiť ich výkonnosť a vyhodnotiť, aký zisk vám skutočne priniesli.
                </p>
                <ul className="flex flex-wrap gap-3">
                  {facts.map(({ icon: Icon, label }) => (
                    <li key={label} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm text-sm font-medium text-[#2A1E47]">
                      <Icon className="w-4 h-4 text-[#FF6B35]" />
                      {label}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap items-center gap-2">
                  <a
                    href="#registracia"
                    className="group inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-[#FF6B35] text-white text-base font-semibold shadow-md shadow-[#FF6B35]/30 hover:bg-[#FF6B35]/90 hover:-translate-y-0.5 hover:shadow-lg transition-all"
                  >
                    Registrovať sa zadarmo
                  </a>
                </div>
              </div>
            </div>
            <div className="reveal">
              <BfSeasonChart program />
            </div>
          </div>
        </section>

        {/* Registration: the only form on the page, every CTA scrolls here */}
        <section id="registracia" className="scroll-mt-4 px-6 py-14 md:py-20 bg-[#2A1E47]">
          <div className="max-w-[var(--maxw)] mx-auto grid md:grid-cols-[1fr_1.1fr] gap-8 md:gap-12 items-start">
            <div className="reveal text-white space-y-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#FF6B35] mb-3">Registrácia</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">Rezervujte si miesto zadarmo</h2>
                <p className="text-white/80">{WEBINAR.dateLabel} · {WEBINAR.timeLabel} · {WEBINAR.durationLabel} · online</p>
                <p className="mt-3 text-white/80 text-sm leading-relaxed max-w-md">
                  Na konci je 15 minút Q&A, kde Martin a Jakub odpovedajú na vaše otázky aj k vašim vlastným číslam.
                </p>
              </div>
            <div className="reveal rounded-2xl border border-white/10 bg-white p-6 md:p-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#FF6B35] mb-4">Kto vás prevedie</p>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-4 mb-2">
                    {photoOk ? (
                      <img src={MARTIN_PHOTO} alt="Martin Faith" onError={() => setPhotoOk(false)} className="w-14 h-14 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-[#2A1E47] text-white flex items-center justify-center text-lg font-bold shrink-0">M</div>
                    )}
                    <div>
                      <p className="font-bold text-[#2A1E47]">Martin Faith</p>
                      <p className="text-sm text-muted-foreground">CEO, Stratodata</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{MARTIN_BIO}</p>
                </div>
                <div>
                  <div className="flex items-center gap-4 mb-2">
                    <img src={GUEST.photo} alt={GUEST.name} className="w-14 h-14 rounded-full object-cover shrink-0" />
                    <div>
                      <p className="font-bold text-[#2A1E47]">{GUEST.name}</p>
                      <p className="text-sm text-muted-foreground">Špeciálny hosť · {GUEST.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{GUEST.bio} Na webinári sa podelí o svoj prístup k Black Friday z praxe.</p>
                </div>
              </div>
            </div>

            </div>
            <div className="reveal p-6 md:p-8 rounded-2xl bg-[#FFF4E8] border border-gray-200 border-t-4 border-t-[#FF6B35] shadow-lg shadow-[#FF6B35]/10">
              {isSuccess ? (
                <div className="text-center py-4">
                  <CheckCircle2 className="w-14 h-14 text-[#FF6B35] mx-auto mb-4 animate-fade-in-up" />
                  <h2 className="text-2xl font-bold text-[#2A1E47] mb-2">Miesto máte rezervované</h2>
                  <p className="text-muted-foreground mb-6">
                    Potvrdenie vám príde e-mailom. Odkaz na pripojenie pošleme deň pred webinárom.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href={googleCalendarUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#2A1E47] text-white font-medium hover:bg-[#2A1E47]/90 transition-colors"
                    >
                      <CalendarPlus className="w-4 h-4" />
                      Google Kalendár
                    </a>
                    <button
                      type="button"
                      onClick={downloadIcs}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-[#2A1E47] text-[#2A1E47] font-medium hover:bg-[#2A1E47] hover:text-white transition-colors"
                    >
                      <CalendarPlus className="w-4 h-4" />
                      Outlook / iCal (.ics)
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
                      {([
                        { name: 'name', label: 'Meno a priezvisko', placeholder: 'Jana Nováková', autoComplete: 'name', type: 'text' },
                        { name: 'email', label: 'Pracovný e-mail', placeholder: 'jana@firma.sk', autoComplete: 'email', type: 'email' },
                        { name: 'company', label: 'Firma', placeholder: 'Názov firmy', autoComplete: 'organization', type: 'text' },
                      ] as const).map((f) => (
                        <FormField
                          key={f.name}
                          control={form.control}
                          name={f.name}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">{f.label}<span className="text-[#FF6B35]" aria-hidden="true"> *</span></FormLabel>
                              <FormControl>
                                <Input type={f.type} placeholder={f.placeholder} autoComplete={f.autoComplete} className="bg-white" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ))}

                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">Pozícia<span className="text-[#FF6B35]" aria-hidden="true"> *</span></FormLabel>
                            <FormControl>
                              <select
                                {...field}
                                className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              >
                                <option value="" disabled>Vyberte…</option>
                                {roles.map((r) => (
                                  <option key={r} value={r}>{r}</option>
                                ))}
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Honeypot */}
                      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" {...form.register('website')} />

                      <FormField
                        control={form.control}
                        name="consent"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex flex-row items-start gap-3">
                              <FormControl>
                                <Checkbox checked={field.value === true} onCheckedChange={(v) => field.onChange(v === true)} />
                              </FormControl>
                              <FormLabel className="text-sm font-normal text-muted-foreground leading-relaxed cursor-pointer">
                                Súhlasím so spracovaním osobných údajov za účelom registrácie a organizácie webinára.<span className="text-[#FF6B35]" aria-hidden="true"> *</span>
                              </FormLabel>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="marketingConsent"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start gap-3 space-y-0">
                            <FormControl>
                              <Checkbox checked={field.value === true} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="text-sm font-normal text-muted-foreground leading-relaxed cursor-pointer">
                              Súhlasím so spracovaním osobných údajov na marketingové účely: Stratodata mi môže posielať pozvánky na webináre a ďalšie e-maily. Súhlas môžem kedykoľvek odvolať.
                            </FormLabel>
                          </FormItem>
                        )}
                      />

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-[#FF6B35] text-white text-base font-semibold shadow-md shadow-[#FF6B35]/30 hover:bg-[#FF6B35]/90 hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60 disabled:hover:translate-y-0"
                      >
                        {isSubmitting ? 'Odosielam…' : 'Registrovať sa zadarmo'}
                      </button>

                      <p className="text-center text-xs">
                        <Link to="/privacy" className="text-muted-foreground underline hover:text-foreground">Ochrana osobných údajov</Link>
                      </p>
                    </form>
                  </Form>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="px-6 py-14 md:py-20">
          <div className="max-w-3xl mx-auto">
            <div className="reveal rounded-2xl border border-gray-100 bg-[#FFF4E8] p-6 md:p-8 flex flex-col">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#FF6B35] mb-4">Ukážka z praxe</p>
              <h3 className="text-xl font-bold text-[#2A1E47] mb-3">Retail Automation Suite: jeden pohľad na maržu</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Na webinári ukážeme aj, ako celý proces riešime v Retail Automation Suite (RAS). RAS spojí dáta z marketingu, webu, ERP a skladu do jedného overeného dátového modelu, ktorý sa aktualizuje každý deň.
              </p>
              <ul className="space-y-2 mb-6 text-muted-foreground">
                {[
                  'Čistý zisk vidíte po predajných a marketingových kanáloch, produktoch, kategóriách aj značkách.',
                  'Počas Black Friday tak viete, ktorá kampaň a ktorý produkt reálne zarába a kde treba pribrzdiť.',
                  'Na dáta sa dajú napojiť automatizácie, napríklad denné nacenenie podľa marže a skladu alebo predpoveď predaja.',
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#FF6B35] shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#registracia"
                className="group mt-auto self-start inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#FF6B35] text-white font-semibold shadow-md shadow-[#FF6B35]/30 hover:bg-[#FF6B35]/90 transition-colors"
              >
                Rezervovať si miesto
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Webinar;
